import { canv } from "@/canv/global.js";
import {
	getColorProperty,
	getNumberProperty,
	getStringProperty,
	lightOrDark,
} from "@/canv/utils.js";
import { elementBase } from "@/elementBase/elementBase.js";
import { widgetOption } from "@/widgetMenu/widgetMenu";

import { textWidget } from "@/elements/text/text.js";

let DRAW_ERROR_LOGGED = false;

export class shapeWidget extends elementBase {
	constructor(startX, startY, width, height) {
		super();

		this.startX = startX;
		this.startY = startY;
		this.width = width;
		this.height = height;

		this.textPart = new textWidget(
			this.startX + this.horizontalPadding,
			this.startY + this.topPadding,
		);

		this.textPart.shapeMode = true;

		this.textPart.menuTextAlign("center", true);
		this.textPart.setKeepMinWidth(false);

		if (lightOrDark(this.color) == "light") {
			this.textPart.menuTextColor("black", true);
		} else {
			this.textPart.menuTextColor("white", true);
		}

		this.updateDims();
	}

	// UpdateDims

	updateDims() {
		this.horizontalPadding = 0.04 * this.width;
		this.verticalPadding = 0.04 * this.height;

		this.allowedBox = canv.shapeStorage
			.getShape(this.mode)
			.fitText(
				this.textPart,
				this.startX,
				this.startY,
				this.width,
				this.height,
				this.verticalPadding,
				this.horizontalPadding,
			);

		this.updateBase(this.startX, this.startY, this.width, this.height);
	}

	// TryAction -------------------------------

	TryAction(type) {
		this.textPart.TryAction(type);
		this.updateDims();

		if (type === "keyboardDown") {
			this.tryShortcut();
		}

		if (type === "mousedown" && !this.textPart.isOver()) {
			if (this.isOver()) {
				canv.toolBox.TryGetToolByName("cursorTool").turnMode("dragging");
			} else {
				canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
				canv.toolBox.TryGetToolByName("cursorTool").TryAction("mousedown");
			}
		}
	}

	isInteraction(context, force = false) {
		if ((context === "focus" && this.isOver()) || force) {
			this.fixState("textFocus");
			this.textPart.focusText();
			return {
				type: "interaction",
			};
		}

		if (context === "insertText" || context === "pastText") {
			this.fixState("textFocus");
			if (canv.keyboard.isEnter()) {
				this.textPart.focusTextByEnter();
			} else if (!canv.keyboard.isEnter()) {
				this.textPart.focusText();
				this.textPart.selection.selectAll();
				this.textPart.processDelete();
				this.TryAction("keyboardDown");
			}
			this.updateDims();
			return {
				type: "interaction",
			};
		}

		return { type: "none" };
	}

	stopInteraction() {
		canv.eventManager.openRecord();
		this.saveState("textFocus");
		canv.eventManager.closeRecord();

		this.textPart.unfocusText();
	}

	tryShortcut() {
		if (canv.keyboard.isEscCommand()) {
			// stopInteraction вызывается внутри cursorTool при изменении mode
			canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
		}
	}

	// --------------------------

	// ClipArea

	clipArea() {
		const textCenterX = this.textPart.startX + this.textPart.width / 2;
		const textCenterY =
			this.textPart.startY -
			this.textPart.lines[0].textHeight +
			this.textPart.height / 2;

		canv.ctx.beginPath();
		canv.ctx.moveTo(
			textCenterX - this.allowedBox["allowedWidth"] / 2,
			textCenterY - this.allowedBox["allowedHeight"] / 2,
		);
		canv.ctx.lineTo(
			textCenterX + this.allowedBox["allowedWidth"] / 2,
			textCenterY - this.allowedBox["allowedHeight"] / 2,
		);
		canv.ctx.lineTo(
			textCenterX + this.allowedBox["allowedWidth"] / 2,
			textCenterY + this.allowedBox["allowedHeight"] / 2,
		);
		canv.ctx.lineTo(
			textCenterX - this.allowedBox["allowedWidth"] / 2,
			textCenterY + this.allowedBox["allowedHeight"] / 2,
		);
		canv.ctx.lineTo(
			textCenterX - this.allowedBox["allowedWidth"] / 2,
			textCenterY - this.allowedBox["allowedHeight"] / 2,
		);

		canv.ctx.clip();
	}

	// Draw

	draw() {
		try {
			canv.shapeStorage
				.getShape(this.mode)
				.draw(this.startX, this.startY, this.width, this.height);
		} catch (e) {
			// в логах был некорректный arcTo. Скорее всего у фигуры rect.
			if (!DRAW_ERROR_LOGGED) {
				DRAW_ERROR_LOGGED = true;
				canv.captureException(e);
			}
			return;
		}

		if (this.color !== "transparent") {
			canv.ctx.fillStyle = this.color;
			canv.ctx.strokeStyle = this.color;
			canv.ctx.lineWidth = 4;
			canv.ctx.fill();
		}

		if (this.borderColor !== "transparent") {
			canv.ctx.strokeStyle = this.borderColor;
			if (this.borderDashType === "solid") {
				canv.ctx.setLineDash([]);
			} else if (this.borderDashType === "dashed") {
				canv.ctx.setLineDash([5 * this.borderWidth, 2 * 5 * this.borderWidth]);
			} else if (this.borderDashType === "dotted") {
				canv.ctx.lineCap = "round";
				canv.ctx.setLineDash([0.00001, 5 * this.borderWidth]);
			}
			canv.ctx.lineWidth = this.borderWidth;
			canv.ctx.stroke();
		}

		canv.ctx.save();

		if (
			!(
				canv.toolBox.TryGetToolByName("cursorTool").isMode("interaction") &&
				canv.canvasController.isInSelected(this.parent)
			)
		) {
			this.clipArea();
		}

		this.textPart.draw();

		canv.ctx.restore();

		canv.ctx.setLineDash([]);
	}

	// translate, resize

	translate(x, y) {
		this.startX += x;
		this.startY += y;
		this.updateDims();
	}

	resize(x, y, catchPointer) {
		const angle = Math.abs(this.parent.angle) % 360;
		const MIN_SIZE = 1;

		if (canv.keyboard.isShift()) {
			const delta = Math.max(x, y); // чтобы сохранять квадратность
			this.width += delta;
			this.height += delta;
		} else {
			if ((angle >= 70 && angle < 160) || (angle >= 250 && angle < 340)) {
				this.height += x;
				this.width += y;
			} else {
				this.height += y;
				this.width += x;
			}
		}
		if (this.width < MIN_SIZE) this.width = MIN_SIZE;
		if (this.height < MIN_SIZE) this.height = MIN_SIZE;

		this.updateDims();
	}

	// helpers

	clearText() {
		this.textPart.clearText();
		this.updateDims();
	}

	searchText(text) {
		const shapeText = this.textPart.getText().toLowerCase();
		if (shapeText.includes(text)) {
			return [
				{
					status: true,
					textWidget: this.textPart,
					mode: "widget",
				},
			];
		}
		return [];
	}

	// Обработчики для меню

	menuBorder(data) {
		var shapeTool = canv.toolBox.TryGetToolByName("shapeTool");

		if (data["borderDashType"] != undefined) {
			this.borderDashType = data["borderDashType"];
			shapeTool.setBorderDashType(this.borderDashType);
		} else if (data["borderColor"] != undefined) {
			this.borderColor = data["borderColor"];
			shapeTool.setBorderColor(this.borderColor);
		} else if (data["borderWidth"] != undefined) {
			this.borderWidth = data["borderWidth"];
			shapeTool.setBorderWidth(this.borderWidth);
		}
	}

	menuColor(color) {
		this.color = color;
		if (lightOrDark(this.color) == "light") {
			this.textPart.menuTextColor("black", true);
		} else {
			this.textPart.menuTextColor("white", true);
		}
		var shapeTool = canv.toolBox.TryGetToolByName("shapeTool");
		shapeTool.setColor(this.color);
	}

	menuShape(shape) {
		this.mode = shape;

		var shapeTool = canv.toolBox.TryGetToolByName("shapeTool");
		shapeTool.setMode(this.mode);

		if (this.isFocused) {
			this.textPart.setCursorToEnd();
		}

		this.updateDims();
	}

	menuTextSize(value) {
		this.textPart.menuTextSize(value);
		this.updateDims();
	}

	menuTextBold(value) {
		this.textPart.menuTextBold(value);
		this.updateDims();
	}

	menuTextStrike(value) {
		this.textPart.menuTextStrike(value);
		this.updateDims();
	}

	menuTextBulleted(value) {
		this.textPart.menuTextBulleted(value);
		this.updateDims();
	}

	menuTextAlign(value) {
		this.textPart.menuTextAlign(value);
		this.updateDims();
	}

	menuLink(currentLink) {
		if (this.textPart.isFocused) {
			this.textPart.menuLink(currentLink, this.updateDims.bind(this));
		} else {
			canv.toolBox
				.TryGetToolByName("cursorTool")
				.setSelectedAndInteraction(this.parent);
			this.textPart.selection.selectAll();
			this.textPart.menuLink(currentLink, this.updateDims.bind(this));
		}
	}

	getWidgetOptions() {
		var widgetOptions = [];
		widgetOptions.push(new widgetOption("shape", this.mode, this.UID));
		widgetOptions.push(new widgetOption("color", this.color, this.UID));

		var data = {
			borderColor: this.borderColor,
			borderDashType: this.borderDashType,
			borderWidth: this.borderWidth,
		};

		widgetOptions.push(new widgetOption("border", data, this.UID));

		widgetOptions.push(
			new widgetOption("textBold", this.textPart.fontBold, this.UID),
		);
		widgetOptions.push(
			new widgetOption("textStrike", this.textPart.fontStrike, this.UID),
		);
		widgetOptions.push(
			new widgetOption("textBulleted", this.textPart.bullettedList, this.UID),
		);
		widgetOptions.push(
			new widgetOption("textAlign", this.textPart.align, this.UID),
		);
		widgetOptions.push(
			new widgetOption("textSize", this.textPart.fontSize, this.UID),
		);

		this.textPart.setAffectedItemsUniqValues(
			widgetOptions,
			"link",
			"link",
			this.UID,
		);
		this.textPart.setEditTextMobile(widgetOptions, this.UID);

		return widgetOptions;
	}

	getHandlerForOption(name) {
		if (name === "shape") {
			return this.menuShape;
		} else if (name === "color") {
			return this.menuColor;
		} else if (name === "textBold") {
			return this.menuTextBold;
		} else if (name === "textStrike") {
			return this.menuTextStrike;
		} else if (name === "textBulleted") {
			return this.menuTextBulleted;
		} else if (name === "textSize") {
			return this.menuTextSize;
		} else if (name === "border") {
			return this.menuBorder;
		} else if (name === "link") {
			return this.menuLink;
		} else if (name === "mobileEdit") {
			return this.textPart.menuMobileEdit;
		} else if (name === "textAlign") {
			return this.menuTextAlign;
		}
	}

	// Далее для statesManager

	getFixType(reason) {
		var fixType = "full";

		switch (reason) {
			case "move":
				fixType = "move";
				break;
			case "resize":
				fixType = "resize";
				break;
			case "wm_shape":
				fixType = "shape";
				break;
			case "wm_color":
				fixType = "color";
				break;
			case "wm_border":
				fixType = "border";
				break;
			case "wm_textBold":
				fixType = "bold";
				if (this.isFocused) fixType = "full";
				break;
			case "wm_textStrike":
				fixType = "strike";
				if (this.isFocused) fixType = "full";
				break;
			case "wm_textBulleted":
				fixType = "bulleted";
				if (this.isFocused) fixType = "full";
				break;
			case "wm_textSize":
				fixType = "size";
				if (this.isFocused) fixType = "full";
				break;
		}

		return fixType;
	}

	getState(fixType) {
		var state = {};

		switch (fixType) {
			case "move":
				state["startX"] = this.startX;
				state["startY"] = this.startY;
				break;
			case "resize":
				state["startX"] = this.startX;
				state["startY"] = this.startY;
				state["width"] = this.width;
				state["height"] = this.height;
				break;
			case "shape":
				state["shape"] = this.mode;
				break;
			case "color":
				state["color"] = this.color;
				break;
			case "border":
				state["borderColor"] = this.borderColor;
				state["borderDashType"] = this.borderDashType;
				state["borderWidth"] = this.borderWidth;
				break;
			case "bold":
				state["textPart"] = this.textPart.getState("full");
				break;
			case "strike":
				state["textPart"] = this.textPart.getState("full");
				break;
			case "bulleted":
				state["textPart"] = this.textPart.getState("full");
				break;
			case "size":
				state["textPart"] = this.textPart.getState("full");
				break;
			case "full":
				state["UID"] = this.UID;
				state["parentUID"] = this.parentUID;
				state["entityType"] = "shapeWidget";
				state["startX"] = this.startX;
				state["startY"] = this.startY;
				state["width"] = this.width;
				state["height"] = this.height;

				state["color"] = this.color;
				state["borderColor"] = this.borderColor;
				state["borderDashType"] = this.borderDashType;
				state["borderWidth"] = this.borderWidth;
				state["mode"] = this.mode;

				state["textPart"] = this.textPart.getState("full");
				break;
		}

		return state;
	}

	loadState(stateObj, fixType) {
		switch (fixType) {
			case "move":
				this.startX = getNumberProperty("startX", stateObj);
				this.startY = getNumberProperty("startY", stateObj);
				break;
			case "resize":
				this.startX = getNumberProperty("startX", stateObj);
				this.startY = getNumberProperty("startY", stateObj);
				this.width = getNumberProperty("width", stateObj);
				this.height = getNumberProperty("height", stateObj);
				break;
			case "shape":
				this.mode = getStringProperty("shape", stateObj);
				break;
			case "color":
				this.color = getColorProperty("color", stateObj);
				if (lightOrDark(this.color) == "light") {
					this.textPart.menuTextColor("black", true);
				} else {
					this.textPart.menuTextColor("white", true);
				}
				break;
			case "border":
				this.borderColor = getColorProperty(
					"borderColor",
					stateObj,
					"transparent",
				);
				this.borderDashType = getStringProperty(
					"borderDashType",
					stateObj,
					"solid",
				);
				this.borderWidth = getNumberProperty("borderWidth", stateObj, 1);
				break;
			case "bold":
				this.textPart.loadState(stateObj["textPart"], "full");
				break;
			case "strike":
				this.textPart.loadState(stateObj["textPart"], "full");
				break;
			case "bulleted":
				this.textPart.loadState(stateObj["textPart"], "full");
				break;
			case "size":
				this.textPart.loadState(stateObj["textPart"], "full");
				break;
			case "full":
				this.UID = getStringProperty("UID", stateObj);
				this.parentUID = getStringProperty("parentUID", stateObj);
				this.startX = getNumberProperty("startX", stateObj);
				this.startY = getNumberProperty("startY", stateObj);
				this.width = getNumberProperty("width", stateObj);
				this.height = getNumberProperty("height", stateObj);
				this.color = getColorProperty("color", stateObj);
				this.borderColor = getColorProperty(
					"borderColor",
					stateObj,
					"transparent",
				);
				this.borderDashType = getStringProperty(
					"borderDashType",
					stateObj,
					"solid",
				);
				this.borderWidth = getNumberProperty("borderWidth", stateObj, 1);
				this.mode = getStringProperty("mode", stateObj);
				this.textPart.loadState(stateObj["textPart"], "full");

				// тут может прийти не правильный цвет текста (например, импорт miro). Для shape нам не важен цвет текста в state.
				// Мы сами его определяем по цвету shape и ставим menuTextColor
				if (lightOrDark(this.color) == "light") {
					this.textPart.menuTextColor("black", true);
				} else {
					this.textPart.menuTextColor("white", true);
				}

				break;
		}

		// Именно два раза
		this.updateDims();
	}

	constructorName = "shape";
	startX = 0;
	startY = 0;
	width = 0;
	height = 0;

    /**
     * @type {textWidget}
    */
	textPart = null;
	verticalPadding = 30;
	horizontalPadding = 30;

	color = "black";
	mode = "rect";

	borderColor = "transparent";
	borderDashType = "solid";
	borderWidth = 1;
	allowedBox = {
		allowedWidth: 0,
		allowedHeight: 0,
	};
}
